import { Direction, Sort } from '../common/page/sort.model';
import type { Comment } from './model/comment.interface';
import { ContentType } from './model/content-type.enum';
import type { Content, Post } from './model/post.interface';
import type { PostFilter } from './model/post-filter.model';
import type { Video } from './model/video.interface';

export class PerksHelper {
  static POSTS_DEFAULT_PAGE_SIZE = 5;
  static TAB_CONTENT_PAGE_SIZE = 12;
  static POSTS_DEFAULT_SORT = Sort.byDirection(Direction.DESC, 'pinned', 'publishedAt');

  static COMMENTS_DEFAULT_PAGE_SIZE = 10;
  static COMMENTS_CHILD_DEFAULT_PAGE_SIZE = 10;
  static COMMENTS_DEFAULT_DEPTH = 3;
  static COMMENTS_DEFAULT_SORT = Sort.byDirection(Direction.DESC, 'createdAt');

  static sortContents(contents: Content[]): Content[] {
    if (!contents || contents.length === 0) {
      return contents;
    }
    return [...contents].sort((a, b) => {
      const aHasOrder = a.contentOrder != null;
      const bHasOrder = b.contentOrder != null;

      // If both have contentOrder, sort by contentOrder ascending
      if (aHasOrder && bHasOrder) {
        return a.contentOrder - b.contentOrder;
      }
      // If only one has contentOrder, it comes first
      if (aHasOrder && !bHasOrder) {
        return -1;
      }
      if (!aHasOrder && bHasOrder) {
        return 1;
      }
      // Fallback to createdAt
      if (a.createdAt && b.createdAt) {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      return 0;
    });
  }

  static sortPostContents(post: Post): Post {
    return {
      ...post,
      contents: PerksHelper.sortContents(post.contents),
    };
  }

  static isAllImages(content: Content[]): boolean {
    return content.every((c) => c.contentType === ContentType.IMAGE);
  }

  static isProduct(content: Content[]): boolean {
    return (
      content?.length === 1 &&
      content[0]?.contentType === ContentType.PRODUCT &&
      !content[0]?.product?.error
    );
  }

  static isAudio(content: Content[]): boolean {
    // An audio post is one AUDIO content plus its IMAGE artwork content, so any AUDIO wins
    return content?.length > 0 && content.some((c) => c.contentType === ContentType.AUDIO);
  }

  static getAudioContent(content: Content[]): Content | undefined {
    return content?.find((c) => c.contentType === ContentType.AUDIO);
  }

  static getAudioArtworkUrl(content: Content[]): string | undefined {
    return content?.find((c) => c.contentType === ContentType.IMAGE)?.url;
  }

  static isVideo(content: Content[]): boolean {
    // TODO: Should only be 1 but sometimes we have dupe video content entries still
    // return content?.length === 1 && content[0]?.contentType === ContentType.VIDEO;
    return content.every((c) => c.contentType === ContentType.VIDEO);
  }

  static getTitle(post: Post): string {
    if (post?.title) return post.title;

    const firstContent = post?.contents?.[0];
    if (!firstContent) return '';

    if (PerksHelper.isProduct(post.contents)) {
      return firstContent.product?.title || '';
    }

    if (PerksHelper.isAudio(post.contents)) {
      return PerksHelper.getAudioContent(post.contents)?.title || '';
    }

    if (PerksHelper.isVideo(post.contents)) {
      return (firstContent as Video).name || '';
    }

    return '';
  }

  static processCommentAction(
    commentId: string,
    comment: Comment,
    action: (c: Comment) => void
  ): void {
    if (comment.id === commentId) {
      action(comment);
    } else if (comment.hasChildren && !!comment.children) {
      comment.children.content?.forEach((child) =>
        this.processCommentAction(commentId, child, action)
      );
    }
  }

  static removeCommentFromTree(comments: Comment[], commentId: string): Comment[] {
    return comments
      .map((comment) => {
        if (comment.hasChildren && !!comment.children) {
          comment.children.content = this.removeCommentFromTree(
            comment.children.content,
            commentId
          );
        }
        return comment;
      })
      .filter((comment) => comment.id !== commentId);
  }

  static hasActiveFilters(filter: PostFilter): boolean {
    if (!filter) return false;

    return (
      !!filter.query ||
      (Array.isArray(filter.types) && filter.types.length > 0) ||
      (Array.isArray(filter.authors) && filter.authors.length > 0) ||
      (Array.isArray(filter.tagIds) && filter.tagIds.length > 0)
    );
  }
}
